import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";

type MapEventType =
  | "pointerdown"
  | "pointerup"
  | "pointermove"
  | "contextmenu"
  | "keyup"
  | "keydown";

export function getGoogleMapEventElement(
  map: google.maps.Map,
  eventType?: MapEventType,
): HTMLElement {
  const mapElement = map.getDiv();

  if (eventType === "keyup" || eventType === "keydown") {
    return mapElement;
  }

  return (
    mapElement.querySelector<HTMLElement>('div[style*="z-index: 3;"]') ??
    mapElement
  );
}

export function shouldForwardPointerRelease(
  activePointerId: number | null,
  event: Pick<PointerEvent, "pointerId" | "target">,
  eventElement: HTMLElement,
): boolean {
  return (
    activePointerId !== null &&
    activePointerId === event.pointerId &&
    event.target !== eventElement
  );
}

export class TowerGoogleMapsAdapter extends TerraDrawGoogleMapsAdapter {
  private readonly towerMap: google.maps.Map;
  private readonly towerPointerElement: HTMLElement;
  private activePointerId: number | null = null;

  private readonly handlePointerDown = (event: PointerEvent) => {
    if (event.isPrimary) this.activePointerId = event.pointerId;
  };

  private readonly handlePointerRelease = (event: PointerEvent) => {
    const shouldForward = shouldForwardPointerRelease(
      this.activePointerId,
      event,
      this.towerPointerElement,
    );
    this.activePointerId = null;

    if (!shouldForward) return;

    this.towerPointerElement.dispatchEvent(
      new PointerEvent("pointerup", {
        bubbles: true,
        button: event.button,
        buttons: 0,
        cancelable: true,
        clientX: event.clientX,
        clientY: event.clientY,
        ctrlKey: event.ctrlKey,
        isPrimary: event.isPrimary,
        metaKey: event.metaKey,
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        shiftKey: event.shiftKey,
      }),
    );
  };

  constructor(
    config: ConstructorParameters<typeof TerraDrawGoogleMapsAdapter>[0],
  ) {
    super(config);
    this.towerMap = config.map;
    this.towerPointerElement = getGoogleMapEventElement(config.map);
  }

  override getMapEventElement(eventType?: MapEventType): HTMLElement {
    if (eventType === "keyup" || eventType === "keydown") {
      return this.towerMap.getDiv();
    }

    return this.towerPointerElement;
  }

  override register(
    callbacks: Parameters<TerraDrawGoogleMapsAdapter["register"]>[0],
  ): void {
    super.register(callbacks);
    this.towerPointerElement.addEventListener(
      "pointerdown",
      this.handlePointerDown,
    );
    window.addEventListener("pointerup", this.handlePointerRelease, true);
    window.addEventListener("pointercancel", this.handlePointerRelease, true);
  }

  override unregister(): void {
    this.towerPointerElement.removeEventListener(
      "pointerdown",
      this.handlePointerDown,
    );
    window.removeEventListener("pointerup", this.handlePointerRelease, true);
    window.removeEventListener(
      "pointercancel",
      this.handlePointerRelease,
      true,
    );
    this.activePointerId = null;
    super.unregister();
  }
}
